/**
 * How well a citation was anchored back to its source, derived from the fields
 * `resolve_anchor` already returns.
 *
 * The model proposes a passage; the server tries to find that passage in the
 * extracted page text and refuses to invent a page when it cannot. That refusal
 * is only useful if the reader can see it — otherwise a citation we failed to
 * verify looks exactly like one we confirmed, which is the failure mode worth
 * designing against here.
 */

import i18n from '@/i18n'

/** Mirrors ANCHOR_MATCH_CONFIDENCE_THRESHOLD in the backend anchor-resolve.ts. */
export const ANCHOR_MATCH_CONFIDENCE_THRESHOLD = 0.55

export type CitationVerification = 'matched' | 'page_only' | 'unlocated'

export interface CitationAnchorFields {
  pageNumber?: number | null
  pageUnknown?: boolean
  unitCharStart?: number | null
  unitCharEnd?: number | null
  anchorConfidence?: number | null
}

export function citationVerification(
  citation: CitationAnchorFields | null | undefined,
): CitationVerification {
  if (!citation) {
    return 'unlocated'
  }

  const hasPage = citation.pageUnknown !== true && citation.pageNumber != null
  if (!hasPage) {
    return 'unlocated'
  }

  const start = citation.unitCharStart
  const end = citation.unitCharEnd
  const passageLocated = start != null && end != null && end > start
  const confident =
    Number(citation.anchorConfidence ?? 0) >= ANCHOR_MATCH_CONFIDENCE_THRESHOLD

  return passageLocated && confident ? 'matched' : 'page_only'
}

export interface CitationVerificationPresentation {
  markerClassName: string
}

const VERIFICATION_COPY_KEYS = {
  matched: {
    label: 'threads.detail.matchedInSource',
    description: 'threads.detail.matchedInSourceHint',
  },
  page_only: {
    label: 'threads.detail.pageMatchOnly',
    description: 'threads.detail.pageMatchOnlyHint',
  },
  unlocated: {
    label: 'threads.detail.unverified',
    description: 'threads.detail.unverifiedHint',
  },
} as const

export function citationVerificationCopy(verification: CitationVerification): {
  label: string
  description: string
} {
  const keys = VERIFICATION_COPY_KEYS[verification]
  return {
    label: i18n.t(keys.label, { ns: 'app' }),
    description: i18n.t(keys.description, { ns: 'app' }),
  }
}

/**
 * Every state is neutral grey: a citation is a pointer to evidence, not brand
 * chrome, so it never takes `--primary`, `--ring` or any token a white-label
 * theme rewrites (`applyThemeBrandTokens`). Verification is carried by the
 * outline alone: solid when the passage or page was found, dashed when the
 * source could not be located. The one-line hint lives in the source card.
 *
 * `muted-foreground` alphas, never `border-border`: in dark mode `--border`
 * is a desaturated teal that would tint the outline.
 */
export const CITATION_VERIFICATION: Record<
  CitationVerification,
  CitationVerificationPresentation
> = {
  matched: {
    markerClassName: 'border border-muted-foreground/30',
  },
  page_only: {
    markerClassName: 'border border-muted-foreground/30',
  },
  unlocated: {
    markerClassName: 'border border-dashed border-muted-foreground/50',
  },
}
